import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useThemeContext } from "../../contexts/ThemeContext";
import { TilesBackground } from "../../components/Auth/TilesBackground";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { mode } = useThemeContext();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess(false);

    try {
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

      const response = await fetch(`${apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server error: ${response.status} ${response.statusText}. ${text.substring(0, 100)}`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `Failed to send reset email: ${response.statusText}`);
      }

      setSuccessMessage(data.message || "If an account exists with this email, a password reset link has been sent.");
      setSuccess(true);
    } catch (error) {
      console.error("Error in forgot password:", error);
      setError(error.message || "Failed to send reset email. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldStyles = () => {
    if (mode === "dark") {
      return {
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#141414",
          borderRadius: "12px",
          transition: "all 0.3s ease",
          "& fieldset": { borderColor: "#333333", borderWidth: "1.5px" },
          "&:hover": {
            backgroundColor: "#1c1c1c",
            "& fieldset": { borderColor: "#4a4a4a" },
          },
          "&.Mui-focused": {
            backgroundColor: "#1c1c1c",
            "& fieldset": { borderColor: "#34d399", borderWidth: "2px" },
          },
          "&.Mui-error": {
            "& fieldset": { borderColor: "#f44336" },
          },
        },
        "& .MuiInputBase-input": {
          color: "#f5f5f5",
          fontFamily: "Instrument Sans, sans-serif",
          fontSize: "14.5px",
          padding: "11px 14px",
          "&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #141414 inset !important",
            WebkitTextFillColor: "#f5f5f5 !important",
            caretColor: "#f5f5f5",
            transition: "background-color 5000s ease-in-out 0s",
          },
          "&:-webkit-autofill:hover": { WebkitBoxShadow: "0 0 0 100px #1c1c1c inset !important" },
          "&:-webkit-autofill:focus": { WebkitBoxShadow: "0 0 0 100px #1c1c1c inset !important" },
        },
        "& .MuiSvgIcon-root": { color: "#6b7280" },
        "& .MuiFormHelperText-root": {
          color: "#9ca3af",
          "&.Mui-error": { color: "#ef4444" },
        },
      };
    }
    return {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#F3F4F6",
        borderRadius: "12px",
        "& fieldset": { borderColor: "#E5E7EB" },
        "&:hover fieldset": { borderColor: "#D1D5DB" },
        "&.Mui-focused fieldset": { borderColor: "#a0eaa3", borderWidth: "2px" },
        "&.Mui-error fieldset": { borderColor: "#f44336" },
      },
      "& .MuiInputBase-input": {
        color: "#111827",
        fontFamily: "Instrument Sans, sans-serif",
        fontSize: "17px",
        padding: "16px 16px",
        "&:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important",
          WebkitTextFillColor: "#111827 !important",
          caretColor: "#111827",
          transition: "background-color 5000s ease-in-out 0s",
        },
        "&:-webkit-autofill:hover": { WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important" },
        "&:-webkit-autofill:focus": { WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important" },
      },
    };
  };

  const accentColor = mode === "dark" ? "#4FC389" : "#44B57C";

  const fieldLabelSx = {
    mb: 0.75,
    fontWeight: 600,
    fontSize: "15px",
    color: mode === "dark" ? "#d1d5db" : "#374151",
    fontFamily: "Instrument Sans, sans-serif",
  };
  const stepHeadingSx = {
    mb: 1,
    lineHeight: 1.2,
    fontWeight: 700,
    fontSize: { xs: "1.6rem", sm: "1.75rem" },
    color: mode === "dark" ? "#f5f5f5" : "#121a34",
    fontFamily: "Instrument Sans, sans-serif",
    textAlign: "center",
  };
  const stepSubheadingSx = {
    mt: 0,
    mb: 3,
    fontSize: "15px",
    lineHeight: 1.6,
    color: mode === "dark" ? "#9ca3af" : "#6b7280",
    fontFamily: "Instrument Sans, sans-serif",
    textAlign: "center",
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Instrument Sans, sans-serif",
        backgroundColor: mode === "dark" ? "#121212" : "#FAFBFC",
      }}
    >
      <TilesBackground
        borderColor={mode === "dark" ? "rgba(255,255,255,0.09)" : "rgba(17,24,39,0.05)"}
        hoverColor={mode === "dark" ? "rgba(79,195,137,0.16)" : "rgba(68,181,124,0.12)"}
      />
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: 460,
            boxSizing: "border-box",
            mx: "auto",
            py: { xs: 4, sm: 5, md: 5.5 },
            px: { xs: 3.5, sm: 5, md: 5.5 },
            borderRadius: 4,
            backgroundColor: mode === "dark" ? "#1E1E1E" : "#FFFFFF",
            border: mode === "dark" ? "2px solid rgba(255,255,255,0.12)" : "none",
            boxShadow:
              mode === "dark"
                ? "0 2px 10px rgba(0,0,0,0.45), 0 30px 80px rgba(0,0,0,0.7)"
                : "0 2px 10px rgba(17,24,39,0.08), 0 30px 70px rgba(17,24,39,0.18)",
          }}
        >
          {success ? (
            <Box>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  mx: "auto",
                  mb: 3.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: mode === "dark" ? "rgba(79,195,137,0.12)" : "rgba(68,181,124,0.1)",
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 34, color: accentColor }} />
              </Box>
              <Typography variant="h6" sx={stepHeadingSx}>
                Check your email
              </Typography>
              <Typography sx={stepSubheadingSx}>
                {successMessage} Please check your inbox and spam folder.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate("/")}
                sx={{
                  py: 1.25,
                  mt: 1,
                  backgroundColor: accentColor,
                  color: "#ffffff",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  fontFamily: "Instrument Sans, sans-serif",
                  boxShadow:
                    mode === "dark"
                      ? "none"
                      : "0 2px 8px rgba(68,181,124,0.25)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "#3aad77" : "#38a06c",
                    boxShadow:
                      mode === "dark"
                        ? "none"
                        : "0 8px 20px rgba(68,181,124,0.35)",
                  },
                }}
              >
                Back to Login
              </Button>
            </Box>
          ) : (
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Typography variant="h6" sx={stepHeadingSx}>
                Forgot Password?
              </Typography>
              <Typography sx={stepSubheadingSx}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              <Typography sx={fieldLabelSx}>Email Address</Typography>
              <TextField
                fullWidth
                name="email"
                placeholder="your@email.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                inputProps={{ maxLength: 254 }}
                error={!!error}
                sx={{ mb: 2.25, ...getFieldStyles() }}
              />

              {error && (
                <Typography
                  sx={{
                    color: mode === "dark" ? "#f87171" : "#EF4444",
                    fontSize: "14px",
                    mb: 2,
                    fontFamily: "Instrument Sans, sans-serif",
                  }}
                >
                  {error}
                </Typography>
              )}

              <Button
                disabled={isLoading}
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  py: 1.25,
                  mt: 0.5,
                  backgroundColor: accentColor,
                  color: "#ffffff",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  fontFamily: "Instrument Sans, sans-serif",
                  boxShadow:
                    mode === "dark"
                      ? "none"
                      : "0 2px 8px rgba(68,181,124,0.25)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "#3aad77" : "#38a06c",
                    boxShadow:
                      mode === "dark"
                        ? "none"
                        : "0 8px 20px rgba(68,181,124,0.35)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: mode === "dark" ? "#1f3d2c" : "#d4f4d6",
                    color: mode === "dark" ? "rgba(79,195,137,0.5)" : "rgba(0,0,0,0.4)",
                  },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <span>Sending…</span>
                    <CircularProgress
                      size={18}
                      sx={{ color: mode === "dark" ? "rgba(79,195,137,0.7)" : "#ffffff" }}
                    />
                  </Box>
                ) : (
                  "Send Reset Link"
                )}
              </Button>

              <Box sx={{ textAlign: "center", mt: 2.25 }}>
                <Button
                  onClick={() => navigate("/")}
                  sx={{
                    color: mode === "dark" ? "#10b981" : "#2D9B39",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    p: 0,
                    minWidth: "auto",
                    fontFamily: "Instrument Sans, sans-serif",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: mode === "dark" ? "#059669" : "#1a7f64",
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
